import React from 'react';
import { render, screen, fireEvent, waitFor } from '@testing-library/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import Candidates from '../Candidates';
import Api from '../API';

// Mock the API
jest.mock('../API');
const mockApi = Api as jest.Mocked<typeof Api>;

// Mock sessionStorage
const mockSessionStorage = {
  getItem: jest.fn(),
  setItem: jest.fn(),
  removeItem: jest.fn(),
  clear: jest.fn()
};
Object.defineProperty(window, 'sessionStorage', {
  value: mockSessionStorage
});

// Mock the GenericTable components
jest.mock('../../Components', () => ({
  StyledSnackbar: ({ message, state }: { message: string; state: string }) => (
    <div data-testid="snackbar" data-state={state}>
      {message}
    </div>
  ),
  ISnackbarInput: {},
  formatUserPermissions: () => ({
    Candidates: {
      'Edit Candidates': true
    }
  }),
  GenericTable: ({
    items,
    isLoadingItems,
    TableActionList,
    pagination,
    onActionItemClick
  }: {
    items: any[];
    isLoadingItems: boolean;
    TableActionList: any[];
    pagination: any;
    onActionItemClick: (action: any, item: any) => void;
  }) => (
    <div data-testid="generic-table">
      <div data-testid="loading">{isLoadingItems ? 'Loading...' : 'Loaded'}</div>
      <div data-testid="items-count">{items?.length || 0}</div>
      <div data-testid="pagination-total">{pagination?.totalItems || 0}</div>
      <div data-testid="pagination-pages">{pagination?.totalPages || 0}</div>
      {items?.map((item: any, index: number) => (
        <div key={item.id || index} data-testid={`candidate-${item.id || index}`}>
          {item.firstname} {item.lastname}
        </div>
      ))}
      {TableActionList?.map((action: any) => (
        <button
          key={action.id}
          data-testid={`action-${action.id}`}
          onClick={() => onActionItemClick?.(action, { id: 1 })}
        >
          {action.label}
        </button>
      ))}
    </div>
  )
}));

// Mock Search component
jest.mock('../../SmartForms/Search', () => {
  return function MockSearch({
    setSearch,
    placeholder,
    helperText
  }: {
    setSearch: (query: string) => void;
    placeholder: string;
    helperText: React.ReactNode;
  }) {
    return (
      <div data-testid="search">
        <input
          data-testid="search-input"
          placeholder={placeholder}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div data-testid="search-helper">{helperText}</div>
      </div>
    );
  };
});

// Mock EditCandidate and DeleteCandidate components
jest.mock('../EditCandidate', () => {
  return function MockEditCandidate({
    editedCandidate,
    isDialogOpen
  }: {
    editedCandidate: any;
    isDialogOpen: boolean;
  }) {
    return isDialogOpen ? (
      <div data-testid="edit-candidate-modal">
        Editing {editedCandidate?.firstname} {editedCandidate?.lastname}
      </div>
    ) : null;
  };
});

jest.mock('../DeleteCandidate', () => {
  return function MockDeleteCandidate({
    isDialogOpen,
    candidateId
  }: {
    isDialogOpen: boolean;
    candidateId: number;
  }) {
    return isDialogOpen ? (
      <div data-testid="delete-candidate-modal">Deleting candidate {candidateId}</div>
    ) : null;
  };
});

const createTestQueryClient = () =>
  new QueryClient({
    defaultOptions: {
      queries: {
        retry: false
      }
    }
  });

const theme = createTheme();

const renderWithProviders = (component: React.ReactElement) => {
  const queryClient = createTestQueryClient();
  return render(
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>{component}</ThemeProvider>
    </QueryClientProvider>
  );
};

describe('Candidates Component', () => {
  beforeEach(() => {
    jest.clearAllMocks();
    mockSessionStorage.getItem.mockReturnValue('1');
  });

  const mockCandidatesData = {
    res: {
      candidates: [
        {
          id: 1,
          firstname: 'John',
          lastname: 'Doe',
          email: 'john.doe@example.com',
          phone1: '1234567890',
          number_of_applications: 2,
          employee_id: null,
          duplicated_candidates: [],
          existing_candidate: false,
          gender_full: 'Male',
          updated_at: '2023-01-01T00:00:00Z',
          linkedin_url: null,
          phone2: '',
          address: '123 Main St',
          tag_list: [],
          entity_tags: [],
          address_details: {
            country: null,
            state: null,
            id: 1,
            postcode: '12345',
            street1: '123 Main St',
            street2: null,
            street_number: '123',
            street_type: 'St',
            suburb: 'Test City'
          },
          notepad: null,
          title: 'Software Engineer'
        },
        {
          id: 2,
          firstname: 'Jane',
          lastname: 'Smith',
          email: 'jane.smith@example.com',
          phone1: '0987654321',
          number_of_applications: 1,
          employee_id: 'EMP001',
          duplicated_candidates: [],
          existing_candidate: true,
          gender_full: 'Female',
          updated_at: '2023-01-02T00:00:00Z',
          linkedin_url: 'https://linkedin.com/in/janesmith',
          phone2: '',
          address: '456 Oak Ave',
          tag_list: ['experienced'],
          entity_tags: [{ value: 'senior', label: 'Senior' }],
          address_details: {
            country: { id: 1, name: 'Australia', iso: 'AU' },
            state: { id: 1, name: 'NSW' },
            id: 2,
            postcode: '2000',
            street1: '456 Oak Ave',
            street2: null,
            street_number: '456',
            street_type: 'Ave',
            suburb: 'Sydney'
          },
          notepad: { body: 'Great candidate' },
          title: 'Senior Developer'
        }
      ]
    },
    resHead: {
      'x-total-count': '2',
      'x-total-pages': '1'
    }
  };

  const mockPreferencesData = {
    res: {
      ui_preferences: [
        {
          row_count: 20,
          sorting: {
            sortBy: 'lastname',
            sortOrder: 'desc'
          }
        }
      ]
    },
    resHead: {}
  };

  const mockDensityData = {
    res: {
      ui_preferences: [
        {
          columns: {
            density: 'Compact'
          }
        }
      ]
    },
    resHead: {}
  };

  it('renders the candidates page with header and search', async () => {
    mockApi.getCandidates.mockResolvedValue(mockCandidatesData);
    mockApi.getPreferences.mockResolvedValue(mockPreferencesData);
    mockApi.getDensityPreferences.mockResolvedValue(mockDensityData);

    renderWithProviders(<Candidates isAdminUser={true} />);

    expect(screen.getByText('Candidates')).toBeInTheDocument();
    expect(screen.getByTestId('search')).toBeInTheDocument();
    expect(screen.getByPlaceholderText('Search by Name, Email or Phone')).toBeInTheDocument();
  });

  it('displays loading state initially', async () => {
    mockApi.getCandidates.mockImplementation(() => new Promise(() => {}));
    mockApi.getPreferences.mockResolvedValue(mockPreferencesData);
    mockApi.getDensityPreferences.mockResolvedValue(mockDensityData);

    renderWithProviders(<Candidates isAdminUser={true} />);

    expect(screen.getByText('Loading...')).toBeInTheDocument();
  });

  it('displays candidates data when loaded', async () => {
    mockApi.getCandidates.mockResolvedValue(mockCandidatesData);
    mockApi.getPreferences.mockResolvedValue(mockPreferencesData);
    mockApi.getDensityPreferences.mockResolvedValue(mockDensityData);

    renderWithProviders(<Candidates isAdminUser={true} />);

    await waitFor(() => {
      expect(screen.getByText('Loaded')).toBeInTheDocument();
      expect(screen.getByText('John Doe')).toBeInTheDocument();
      expect(screen.getByText('Jane Smith')).toBeInTheDocument();
      expect(screen.getByTestId('items-count')).toHaveTextContent('2');
    });
  });

  it('handles search input changes', async () => {
    mockApi.getCandidates.mockResolvedValue(mockCandidatesData);
    mockApi.getPreferences.mockResolvedValue(mockPreferencesData);
    mockApi.getDensityPreferences.mockResolvedValue(mockDensityData);

    renderWithProviders(<Candidates isAdminUser={true} />);

    const searchInput = screen.getByTestId('search-input');
    fireEvent.change(searchInput, { target: { value: 'John' } });

    await waitFor(() => {
      expect(mockApi.getCandidates).toHaveBeenCalledWith(
        expect.objectContaining({
          'q[search]': 'John'
        })
      );
    });
  });

  it('shows admin actions when user is admin', async () => {
    mockApi.getCandidates.mockResolvedValue(mockCandidatesData);
    mockApi.getPreferences.mockResolvedValue(mockPreferencesData);
    mockApi.getDensityPreferences.mockResolvedValue(mockDensityData);

    renderWithProviders(<Candidates isAdminUser={true} />);

    await waitFor(() => {
      expect(screen.getByTestId('action-edit')).toBeInTheDocument();
      expect(screen.getByTestId('action-delete')).toBeInTheDocument();
    });
  });

  it('shows limited actions when user is not admin but has edit permissions', async () => {
    mockApi.getCandidates.mockResolvedValue(mockCandidatesData);
    mockApi.getPreferences.mockResolvedValue(mockPreferencesData);
    mockApi.getDensityPreferences.mockResolvedValue(mockDensityData);

    renderWithProviders(<Candidates isAdminUser={false} />);

    await waitFor(() => {
      expect(screen.getByTestId('action-edit')).toBeInTheDocument();
      expect(screen.queryByTestId('action-delete')).not.toBeInTheDocument();
    });
  });

  it('handles API errors gracefully', async () => {
    const consoleError = jest.spyOn(console, 'error').mockImplementation(() => undefined);
    mockApi.getCandidates.mockResolvedValue(mockCandidatesData);
    mockApi.getPreferences.mockRejectedValue(new Error('API Error'));
    mockApi.getDensityPreferences.mockResolvedValue(mockDensityData);

    renderWithProviders(<Candidates isAdminUser={true} />);

    await waitFor(() => {
      expect(screen.getByTestId('snackbar')).toHaveAttribute('data-state', 'error');
    });

    consoleError.mockRestore();
  });

  it('displays search helper text with tip', async () => {
    mockApi.getCandidates.mockResolvedValue(mockCandidatesData);
    mockApi.getPreferences.mockResolvedValue(mockPreferencesData);
    mockApi.getDensityPreferences.mockResolvedValue(mockDensityData);

    renderWithProviders(<Candidates isAdminUser={true} />);

    expect(screen.getByText('Tip:')).toBeInTheDocument();
    expect(screen.getByText(/When searching by phone number/)).toBeInTheDocument();
    expect(screen.getByText('Example: 0491570006 or 61491570006')).toBeInTheDocument();
  });

  it('updates session storage with current page', async () => {
    mockApi.getCandidates.mockResolvedValue(mockCandidatesData);
    mockApi.getPreferences.mockResolvedValue(mockPreferencesData);
    mockApi.getDensityPreferences.mockResolvedValue(mockDensityData);

    renderWithProviders(<Candidates isAdminUser={true} />);

    await waitFor(() => {
      expect(mockSessionStorage.setItem).toHaveBeenCalledWith('candidatesTablePage', '1');
    });
  });
});
